import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Compass, Eye, EyeOff, LogIn } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";

const LOGIN_BG = "https://images.pexels.com/photos/10611880/pexels-photo-10611880.jpeg";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (isAuthenticated) navigate("/app", { replace: true });
  }, [isAuthenticated, navigate]);

  const doLogin = async (em, pw) => {
    setSubmitting(true);
    try {
      const data = await login(em.trim(), pw);
      toast.success(`Bienvenida/o, ${data.user.full_name.split(" ")[0]}`);
      navigate("/app", { replace: true });
    } catch (err) {
      toast.error(apiErrorMessage(err, "No se pudo iniciar sesión"));
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Introduce email y contraseña");
      return;
    }
    await doLogin(email, password);
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Visual side */}
      <div className="relative hidden w-1/2 overflow-hidden lg:block">
        <img src={LOGIN_BG} alt="Destino de viaje premium" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E2320] via-[#1E2320]/40 to-transparent" />
        <div className="absolute bottom-0 left-0 p-12 text-white">
          <div className="mb-4 flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#C45A45] shadow-lg">
              <Compass className="h-5 w-5" />
            </div>
            <span className="font-heading text-xl font-semibold">Zicasso</span>
          </div>
          <h2 className="font-heading text-4xl font-semibold leading-tight tracking-tight">
            Cada lead es<br />un viaje por diseñar.
          </h2>
          <p className="mt-3 max-w-md text-sm text-white/70">
            Gestiona tus leads de Zicasso Partners, sigue el timeline oficial y convierte
            más viajes — todo en un solo lugar.
          </p>
        </div>
      </div>

      {/* Form side */}
      <div className="flex w-full items-center justify-center px-6 lg:w-1/2">
        <div className="w-full max-w-sm zc-fade-up">
          <div className="mb-8 lg:hidden">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#C45A45] text-white shadow-lg">
              <Compass className="h-6 w-6" />
            </div>
          </div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Inicia sesión</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Accede a tu panel de leads de Zicasso.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" type="email" autoComplete="username"
                placeholder="tu@espiritutravel.com"
                value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password" type={showPw ? "text" : "password"} autoComplete="current-password"
                  placeholder="••••••••"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground"
                  aria-label={showPw ? "Ocultar" : "Mostrar"}
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={submitting} data-testid="login-submit-button">
              <LogIn className="mr-2 h-4 w-4" />
              {submitting ? "Entrando…" : "Iniciar sesión"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
